import { useState } from 'react';
import { useLanguage } from '../i18n/useLanguage';
import PlaceholderImage from '../components/PlaceholderImage';
import Reveal from '../components/Reveal';

// Per Figma 82:71 the design shows seven tabs across the top of the
// folder — one selected, six unselected. Keep the count and selection
// model client-side; the actual per-feature copy comes from i18n once
// it's filled in. Until then each tab cycles a distinct placeholder
// image so the carousel is visibly interactive even with stub content.
const TAB_COUNT = 7;

export default function FeaturesSection() {
  const { lang, t } = useLanguage();
  const [active, setActive] = useState(0);

  const prev = () => setActive((a) => (a - 1 + TAB_COUNT) % TAB_COUNT);
  const next = () => setActive((a) => (a + 1) % TAB_COUNT);

  // Per-feature artwork lives at `/images/feature/<lang>/<NN>-<orientation>.png`:
  // the parent folder picks the language (en / th), the two-digit
  // index matches the active tab, and the orientation suffix swaps
  // between the desktop landscape variant and the mobile portrait
  // companion. Dropping in a new screenshot only means saving two
  // PNGs per language into the right folder with this name shape.
  const slot = String(active + 1).padStart(2, '0');
  const featureBase = `/images/feature/${lang}/${slot}`;
  const featureImgDesktopSrc = `${featureBase}-landscape.png`;
  const featureImgMobileSrc = `${featureBase}-portrait.png`;

  return (
    <section
      id="features"
      // Mobile-specific scroll behaviour (mirrors Register / Story):
      //   • `!min-h-screen` (important) forces the section to
      //     ≥ 100vh on phones. The `!` is required because
      //     `.snap-section { min-height: 80vh }` lives outside
      //     `@layer` in index.css and is declared after
      //     `@tailwind utilities`, so on source-order it would
      //     normally win over a plain `min-h-screen`.
      //   • `justify-center` is dropped on mobile (so the panel
      //     stacks from the top, putting the rotated portrait
      //     frame high in the section) and only applies at md+.
      //   • The mobile portrait Reveal wrapper further below
      //     carries its own `snap-start [scroll-margin-top:60px]`
      //     so when mandatory snap settles the frame's top lands
      //     just below the floating header rather than the
      //     section's outer padding doing the offset.
      className="snap-section relative flex !min-h-screen flex-col items-center overflow-hidden bg-section-feature text-text-inverse md:justify-center"
      style={{
        // Same diamond-lattice overlay as StorySection: the brown
        // section-feature (#634645) shows through the SVG's empty
        // interior and the white-at-0.2-opacity stroke draws the
        // diamond grid across the whole section. Painted by the
        // browser BEFORE any child content, so section4_bg.png
        // (rendered as an absolutely-positioned <img> below) sits on
        // top of the pattern. On mobile the wide 1.28:1 background
        // image only covers a horizontal band in the middle of the
        // tall section, and this diamond pattern fills the area
        // above and below it instead of leaving a flat brown.
        backgroundImage: "url('/images/diamond-tile.svg')",
        backgroundRepeat: 'repeat',
        backgroundSize: '64px 64px',
      }}
    >
      {/* Background — section4_bg.png painted full-width with its
          native 3840×3000 (≈1.28:1) aspect preserved. When the section
          is wider in aspect than the image (e.g. typical 16:9
          desktops), the image's height exceeds the section's height
          and the overflow is split equally between top and bottom via
          `top-1/2 -translate-y-1/2` (vertical centring) combined with
          the section's `overflow-hidden`.

          Rendered as an absolutely-positioned <img> rather than a CSS
          background-image specifically so we can use native
          `loading="lazy"` — important because the PNG is ~1.9 MB and
          this is the 4th section, well below the fold. */}
      <img
        src="/images/section4_bg.png"
        alt=""
        aria-hidden="true"
        loading="lazy"
        decoding="async"
        fetchPriority="low"
        className="pointer-events-none absolute left-0 right-0 top-1/2 hidden h-auto w-full max-w-none -translate-y-1/2 select-none lg:block"
        draggable={false}
      />

      {/* Outer content panel — mirrors the cream main panel in
          Register / Story. Sits at z-10 so the whole content tree
          stacks above the absolutely-positioned bg image (which
          renders at z-auto = 0).

          Mobile behaviour (mirrors Register / Story mobile):
            • `justify-start` + reduced `py-4` so the portrait
              frame sits near the top of the section, just under
              the floating header (the frame's own
              scroll-margin-top:60px finishes the alignment).
            • `min-h` constraints DROPPED on mobile — section's
              `!min-h-screen` floors total height at 100vh, but
              the panel inside hugs its content so empty space
              falls at the BOTTOM of the section rather than
              padding both above and below the frame.
          From md+ the original `min-h-[115vh]` + `justify-center`
          come back so the wide landscape scene stays anchored
          full-viewport. */}
      <div className="relative mt-6 lg:mt-0 min-h-[calc(min(100vh_-_256px))] z-10 flex w-full max-w-[1874px] flex-col items-center justify-center px-4 py-6 md:min-h-[calc(115vh)] md:pb-10 lg:min-h-[calc(125vh)]">
        {/* Inner content frame — the "Game Features" scene from
            Figma 82:71. Sized to the Figma frame's 1239×851 aspect so
            every absolutely-positioned child can use stable
            percentage offsets and the whole composition scales as a
            single piece.

            Responsive sizing strategy:
              • On mobile (under md) we keep a max-h-[85vh] cap so a
                tall narrow phone doesn't blow the frame past the
                viewport.
              • On md and up the cap is dropped — aspect-ratio drives
                the height directly from the available width, so the
                frame always renders as a faithful scaled-down copy
                of the desktop layout instead of being squeezed in
                width by an aggressive vh cap (the bug this fixes:
                on 1366×768 / 1440×900 laptops the 85vh cap would
                force the frame to ~70% of the available width and
                visibly shrink everything inside). The section may
                end up slightly taller than 100vh on those short
                viewports; the section's own min-h-[calc(100vh)]
                lets it grow, and snap-scroll still works because
                each section snaps to its start. */}
        {/* Desktop / tablet-landscape layout — Figma node 120:64.
            Wide landscape composition with tabs across the top and
            the file folder dominant in the centre. Hidden below lg
            in favour of the rotated mobile variant rendered after
            this block. */}
        <Reveal className="bg-[#63464533]/90 rounded-3xl mt-0 xl:mb-[7%] px-10 pt-2 relative mx-auto hidden md:w-[75%] lg:w-[75%] max-w-[1280px] lg:block">
          <div className="relative w-full aspect-[1239/851] mx-auto">
            {/* Dark rounded backdrop (BG 20% in Figma) — covers the
                top 96.4% of the frame, leaving the bottom strip empty
                for the fork-spoon decorative row. The brown is the
                section's own colour at 20% opacity so the section4_bg
                pattern still bleeds through. */}
            <div
              aria-hidden="true"
              className="absolute left-0 right-0 top-0 h-[96.4%] rounded-[20px] bg-section-feature/20"
            />

            {/* Title — "แนะนำเกม" anchored to the upper-left of the
                frame, bottom-aligned so the descenders sit on the
                top edge of the file folder below. Uses the
                .features-title-shadow rule (an independent twin of
                .register-title-shadow) so any Features-specific
                drift in stroke width, shadow depth, or font sizing
                stays scoped to this section without touching the
                shared Register treatment. */}
            <div className="absolute left-[3%] top-[3.5%] z-30 flex h-[5%] w-[25%] items-end justify-center pb-1">
              <h2 className="features-title-shadow whitespace-nowrap text-center font-display text-2xl font-extrabold text-white md:text-4xl lg:text-[48px] 2xl:text-[58px] 2xl:font-black tracking-tighter ">
                {t('features.title')}
              </h2>
            </div>

            {/* Page-stack (Menu/Group 11350 in Figma) — three layered
                cream sheets that peek out behind the file folder to
                give the folder a chunky, multi-page feel. Sits at the
                BACK of the inner frame so the tabs (which slot into
                its top edge) and the file folder both render in
                front. */}
            <img
              src="/images/section4_inner_header_right.png"
              alt=""
              aria-hidden="true"
              className="pointer-events-none absolute right-[8%] top-[5%] z-10 w-[60%] select-none"
              draggable={false}
            />

            {/* Tabs row — seven notebook-style tabs anchored to the
                right edge of the frame so they cluster at the top
                right per the design. The selected tab (Figma 83:338)
                uses section4-selected-tab.png (176×212, aspect 0.83
                — portrait) and the unselected ones use
                secetion4-unselect-tab.png (176×174, aspect ≈ 1 —
                nearly square); when both are rendered at the same
                WIDTH the selected tab naturally ends up ~21% taller
                because of the different aspects, so we get the
                Figma "selected tab pokes up" treatment for free
                without any extra scale or special-case markup.
                `items-end` bottom-aligns every tab so the shorter
                unselected ones sit on the same baseline as the
                taller selected one. */}
            <div className="absolute right-[10%] top-[0%] z-20 flex w-[60%] items-end justify-end gap-[0.8%]">
              {Array.from({ length: TAB_COUNT }).map((_, i) => {
                const isActive = i === active;
                return (
                  <button
                    key={i}
                    type="button"
                    onClick={() => setActive(i)}
                    aria-pressed={isActive}
                    aria-label={`Feature ${i + 1}`}
                    className="block w-[12%] shrink-0 cursor-pointer transition-transform duration-200 ease-out hover:-translate-y-[2px]"
                  >
                    <img
                      src={
                        isActive
                          ? '/images/section4-selected-tab.png'
                          : '/images/secetion4-unselect-tab.png'
                      }
                      alt=""
                      aria-hidden="true"
                      className="block h-auto w-full select-none"
                      draggable={false}
                    />
                  </button>
                );
              })}
            </div>

            {/* File folder — pre-rendered as section4_inner_frame.png
                (native 2060×1448, ≈1.423:1). The single asset bakes
                in everything the previous CSS build did by hand: the
                brown 4px border, cream/yellow interior, the two tan
                dashed accent lines, the bookmark ribbon hanging off
                the bottom-left, AND the gray bottom strip with the
                fork-spoon icons. Width-anchored at 87% of the inner
                frame with `aspect-[2060/1448]` so the image's height
                derives from the asset's true aspect; this lets us
                use stable percentage insets when positioning the
                screenshot over the cream area without having to
                second-guess what the browser will do under
                object-contain. */}
            <div className="absolute left-1/2 top-[10%] z-20 w-[87%] aspect-[2060/1448] -translate-x-1/2">
              <img
                src="/images/section4_inner_frame.png"
                alt=""
                aria-hidden="true"
                className="pointer-events-none absolute inset-0 h-full w-full select-none"
                draggable={false}
              />

              {/* Screenshot — positioned over the cream inset of the
                  folder image. Percentages are relative to this
                  wrapper, which shares the image's 2060×1448 aspect,
                  so the screenshot tracks the painted inset region
                  cleanly at any frame size. The bottom inset is
                  larger than the top because the asset reserves the
                  lower ~32% for the dashed line + bottom fork-spoon
                  strip + bookmark. */}
              {/* Cream area is wider than 16:9 (~2.07:1), so we
                  constrain the screenshot to a height-filled
                  aspect-[16/9] box and centre it horizontally —
                  preserves the source image's native 16:9 aspect
                  with a small cream margin on either side. */}
              <div className="absolute inset-x-[5%] top-[7%] bottom-[21%] z-10 flex items-center justify-center">
                <div className="aspect-[16/9] h-full overflow-hidden rounded-[16px] bg-background-tan">
                  <PlaceholderImage
                    src={featureImgDesktopSrc}
                    alt={`Feature ${active + 1}`}
                    hint={`feature ${active + 1} / ${TAB_COUNT}`}
                    className="h-full w-full object-cover"
                  />
                </div>
              </div>
            </div>

            {/* Cat-paw nav — previous. Sits at the vertical centre
                of the dark backdrop, just outside the left edge so
                the paw lightly overlaps the file folder. Width is
                a % of the inner frame (not vw) so the paw scales
                in lockstep with everything else in the scene — on
                mid-sized viewports where the frame is narrower
                than its 1239px design width the paw shrinks
                proportionally instead of jumping to a vw-based
                clamp that's calibrated for the viewport, not the
                frame. */}
            <button
              type="button"
              onClick={prev}
              aria-label="Previous feature"
              className="absolute left-[-2%] top-1/2 z-30 w-[6.5%] -translate-y-1/2 transition-transform duration-200 ease-out hover:scale-110 active:scale-95"
            >
              <img
                src="/images/cat_paw.png"
                alt=""
                aria-hidden="true"
                className="block h-auto w-full select-none"
                draggable={false}
              />
            </button>

            {/* Cat-paw nav — next. Same as the previous button but
                mirrored on the X axis so the paw "points" outward
                on each side. */}
            <button
              type="button"
              onClick={next}
              aria-label="Next feature"
              className="absolute right-[-2%] top-1/2 z-30 w-[6.5%] -translate-y-1/2 transition-transform duration-200 ease-out hover:scale-110 active:scale-95"
            >
              <img
                src="/images/cat_paw.png"
                alt=""
                aria-hidden="true"
                className="block h-auto w-full -scale-x-100 select-none"
                draggable={false}
              />
            </button>

          </div>
        </Reveal>

        {/* Mobile / tablet-portrait layout — Figma node 120:547.
            Same file-folder, tab and page-stack assets as desktop,
            but rotated 90° clockwise so the scene reads as a
            portrait card: bookmark moves from bottom-left to
            top-left, the dashed fork-spoon strip moves from the
            bottom edge to the left edge, and the tabs stack
            vertically along the right edge with the selected tab's
            stem poking LEFT into the folder (instead of poking UP).
            Visible below md only.

            `snap-start [scroll-margin-top:60px]` makes this
            Reveal the mobile section's snap target (mirrors
            Register / Story mobile). When mandatory snap settles
            on mobile the frame's top lands 60px below the
            viewport top — just below the ≈48px floating header.
            Harmless at md+ since the section is hidden then and
            scroll-snap is disabled at md+ on <html>. */}
        <Reveal className="relative mx-auto block w-full max-w-[1024px] snap-start [scroll-margin-top:60px] lg:hidden">
          <div className="relative mx-auto w-full aspect-[648/912] md:max-w-[80%]">
            {/* Title — sits ABOVE the dark backdrop in the mobile
                layout, centred horizontally. Reuses the same
                .register-title-shadow + font-display treatment as
                Register / Story / desktop-Features so the four
                campaign scenes share one title style. */}
            <div className="absolute inset-x-0 top-[1%] z-30 flex h-[6%] items-center justify-center">
              <h2 className="register-title-shadow whitespace-nowrap text-center font-display text-3xl font-extrabold text-white sm:text-4xl">
                {t('features.title')}
              </h2>
            </div>

            {/* Dark rounded backdrop. Starts below the title and
                fills the rest of the frame (matches Figma 120:548
                BG 20% — 648×~820 inside the 648×912 frame). */}
            <div
              aria-hidden="true"
              className="absolute inset-x-0 bottom-0 top-[10%] rounded-[20px] bg-section-feature/20"
            />

            {/* Page-stack peeking from behind the right-side tabs.
                Same asset as desktop, rotated 90° CW so the cream
                sheets fan out vertically along the tab strip
                instead of horizontally across the top. */}
            <img
              src="/images/section4_inner_header_right.png"
              alt=""
              aria-hidden="true"
              className="pointer-events-none absolute right-0 top-1/2 z-10 w-[55%] origin-center -translate-y-1/2 rotate-90 select-none"
              draggable={false}
            />

            {/* File folder — same section4_inner_frame.png as
                desktop (native 2060×1448 landscape) rotated 90° CW.
                Wrapper aspect is the rotated 1448/2060 portrait;
                the inner image is sized so its CSS box matches the
                natural landscape dimensions (width 100% × 2060/1448
                ≈ 142%, aspect-ratio 2060/1448) and is then rotated
                around centre — net effect is a portrait file folder
                that visually fills the wrapper exactly with the
                bookmark at top-left and the fork-spoon strip on the
                left edge. */}
            <div className="absolute left-[45%] top-[12%] z-20 h-[80%] aspect-[1448/2060] -translate-x-1/2">
              <div className="relative h-full w-full">
                <img
                  src="/images/section4_inner_frame.png"
                  alt=""
                  aria-hidden="true"
                  className="pointer-events-none absolute left-1/2 top-1/2 max-w-none origin-center -translate-x-1/2 -translate-y-1/2 rotate-90 select-none"
                  style={{
                    width: 'calc(100% * 2060 / 1448)',
                    aspectRatio: '2060 / 1448',
                  }}
                  draggable={false}
                />

                {/* Screenshot — covers the cream area of the rotated
                    folder. Original landscape insets were
                    inset-x-[5%] top-[7%] bottom-[31%]; rotated 90°
                    CW those map to top:5%, bottom:5%, right:7%,
                    left:31% in portrait coords (the left 31% is the
                    rotated bottom strip with bookmark + dashed line
                    + fork-spoon icons). */}
                {/* Mobile cream area is portrait, so the
                    screenshot box is 9:16 to match — width-bound
                    so it fills the cream's width and centres
                    vertically. The 16:9 source image is rendered
                    via object-cover, which crops the landscape
                    sides off to fit the portrait box. */}
                <div className="absolute bottom-[4%] left-[19%] right-[5%] top-[5%] z-10 flex items-center justify-center">
                  <div className="aspect-[9/16] w-full overflow-hidden rounded-[16px] bg-background-tan">
                    <PlaceholderImage
                      src={featureImgMobileSrc}
                      alt={`Feature ${active + 1}`}
                      hint={`feature ${active + 1} / ${TAB_COUNT}`}
                      className="h-full w-full object-cover"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Tabs — vertical column on the right edge. Each tab
                button reuses the same selected/unselected PNGs as
                desktop but the image is rotated 90° CW inside a
                wrapper sized to the rotated visual aspect. The
                button heights are uniform (10% of column height)
                while widths differ via aspect-ratio: selected
                212/176 (taller-than-wide image becomes wider after
                rotation) → pokes LEFT past the unselected ones
                (174/176 ≈ square), giving the same "selected tab
                sticks out" treatment as desktop's poke-up effect.
                items-end keeps every tab's right edge flush with
                the column's right boundary. */}
            <div className="absolute bottom-[10%] right-[-2%] top-[14%] z-20 flex w-[18%] flex-col items-start justify-end gap-[0.5%]">
              {Array.from({ length: TAB_COUNT }).map((_, i) => {
                const isActive = i === active;
                return (
                  <button
                    key={i}
                    type="button"
                    onClick={() => setActive(i)}
                    aria-pressed={isActive}
                    aria-label={`Feature ${i + 1}`}
                    className="relative block h-[10%] shrink-0 cursor-pointer transition-transform duration-200 ease-out hover:-translate-x-[2px]"
                    style={{
                      aspectRatio: isActive ? '212 / 176' : '174 / 176',
                    }}
                  >
                    <img
                      src={
                        isActive
                          ? '/images/section4-selected-tab.png'
                          : '/images/secetion4-unselect-tab.png'
                      }
                      alt=""
                      aria-hidden="true"
                      className="absolute left-1/2 top-1/2 max-w-none origin-center -translate-x-1/2 -translate-y-1/2 rotate-90 select-none"
                      style={{
                        width: isActive
                          ? 'calc(100% * 176 / 212)'
                          : 'calc(100% * 176 / 174)',
                        aspectRatio: isActive ? '176 / 212' : '176 / 174',
                      }}
                      draggable={false}
                    />
                  </button>
                );
              })}
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
